"use client"

import { useState } from "react"
import { AnimatePresence, motion } from "motion/react"
import { Minus, Plus } from "lucide-react"
import { cn } from "@/utils/helpers/utils"
import { FAQProps, FAQColors, FAQItemRowProps } from "@/lib/faq/FAQProps"

export default function FAQ({ items, color = "blue", title, subtitle }: FAQProps) {
   const [open, setOpen] = useState<string | null>(null)
   const styles = FAQColors[color]

   return (
      <section className="mx-auto  scroll-mt-20 py-16" id="faq">
         {(title || subtitle) && (
            <div className="mb-8 px-2">
               {title && <h2 className="text-3xl font-black tracking-tight text-slate-900">{title}</h2>}
               {subtitle && <p className="mt-2 text-slate-500">{subtitle}</p>}
            </div>
         )}

         <div className="overflow-hidden rounded-[22px] border border-slate-200 bg-white shadow-sm">
            {items.map((faq, index) => (
               <FAQItemRow
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={open === faq.question}
                  toggle={() => setOpen(open === faq.question ? null : faq.question)}
                  isLast={index === items.length - 1}
                  styles={styles}
               />
            ))}
         </div>
      </section>
   )
}

function FAQItemRow({ question, answer, isOpen, toggle, isLast, styles }: FAQItemRowProps) {
   return (
      <div className={cn("transition-colors", !isLast && "border-b border-slate-200")}>
         <button
            className="group flex w-full items-center justify-between gap-4 p-5 text-left hover:bg-slate-50/50"
            onClick={toggle}
         >
            <span
               className={cn(
                  "text-[20px] font-bold tracking-tight transition-colors duration-200",
                  isOpen ? styles.text : "text-slate-800",
               )}
            >
               {question}
            </span>
            <div
               className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-slate-200 bg-white transition-colors duration-200",
                  styles.border,
               )}
            >
               {isOpen ? (
                  <Minus className={cn("h-4 w-4", styles.text)} strokeWidth={3} />
               ) : (
                  <Plus className={cn("h-4 w-4 text-slate-400", styles.iconPlus)} strokeWidth={3} />
               )}
            </div>
         </button>

         <AnimatePresence initial={false}>
            {isOpen && (
               <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="overflow-hidden"
               >
                  <div className="px-5 py-3 text-custom-base-medium text-[#0A0A0A]">{answer}</div>
               </motion.div>
            )}
         </AnimatePresence>
      </div>
   )
}
